import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { url } from "../../utilis/contant";

const TABS = ["Overview", "Subscriptions", "Teachers", "Upcoming"];

const fmt$ = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

const fmtDate = (d) => {
  if (!d) return "N/A";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const fmtTime = (t) => {
  if (!t) return "N/A";
  const [h, m] = t.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

const gold    = "#d5c93d";
const gold10  = "#e6be5d18";
const gold30  = "#e6be5d44";
const border  = "#1a1500";
const surface = "#fff";
const deep    = "#080800";
const muted   = "#6b5e30";
const faint   = "#3a3020";
const dimmer  = "#4a3f1a";

const StatCard = ({ label, value, sub, accent = gold }) => (
  <div style={{ background: surface, border: `1px solid ${border}`, borderRadius: 10, padding: "1.1rem 1.2rem", position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: accent }} />
    <p style={{ fontSize: "1.4rem", fontWeight: 600, color: accent, margin: "0 0 4px" }}>{value}</p>
    <p style={{ fontSize: 11, color: muted, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", margin: 0 }}>{label}</p>
    {sub && <p style={{ fontSize: 11, color: faint, margin: "4px 0 0" }}>{sub}</p>}
  </div>
);

const Badge = ({ text, type = "gold" }) => {
  const styles = {
    gold:   { bg: gold10,       border: gold30,       color: gold },
    silver: { bg: "#8181f118",  border: "#8181f144",  color: "#9090c0" },
    none:   { bg: "#33333318",  border: "#44444444",  color: "#555" },
  };
  const st = styles[type] || styles.none;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600,
      padding: "3px 12px", borderRadius: 20, textTransform: "capitalize",
      background: st.bg, border: `1px solid ${st.border}`, color: st.color,
    }}>
      {text}
    </span>
  );
};

const SectionTitle = ({ children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: ".9rem" }}>
    <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".1em", color: muted, textTransform: "uppercase", whiteSpace: "nowrap" }}>{children}</span>
    <div style={{ flex: 1, height: 1, background: border }} />
  </div>
);

const Th = ({ children }) => (
  <th style={{ background: deep, color: muted, fontWeight: 600, padding: "9px 12px", textAlign: "left", borderBottom: `1px solid ${border}`, letterSpacing: ".06em", textTransform: "uppercase", fontSize: 11, whiteSpace: "nowrap" }}>
    {children}
  </th>
);

const Td = ({ children, isGold, isDim }) => (
  <td style={{ color: isGold ? gold : isDim ? dimmer : "#a09060", padding: "9px 12px", borderBottom: "1px solid #111", whiteSpace: "nowrap", fontWeight: isGold ? 600 : 400 }}>
    {children}
  </td>
);

const Empty = ({ text }) => (
  <div style={{ textAlign: "center", padding: "3rem", background: surface, borderRadius: 10, border: `1px solid ${border}` }}>
    <p style={{ fontSize: "1.8rem", marginBottom: 8 }}>📭</p>
    <p style={{ fontSize: 12, color: faint }}>{text}</p>
  </div>
);

const AdminDashboard = () => {
  const { token } = useSelector((s) => s.auth);
  const [tab, setTab] = useState("Overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${url}/api/v1/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include",
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.message);
        setData(json.data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <div style={{ width: 36, height: 36, border: `3px solid ${border}`, borderTop: `3px solid ${gold}`, borderRadius: "50%", animation: "spin .8s linear infinite" }} />
      <p style={{ color: faint, marginTop: 12, fontSize: 13 }}>Loading dashboard...</p>
    </div>
  );

  if (error) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <p style={{ color: "#ef4444", fontSize: 14 }}>❌ {error}</p>
    </div>
  );

  const { overview, subscriptions, teacherStats, upcomingSessions } = data;

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1100, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "1.5rem", paddingBottom: "1.2rem", borderBottom: `1px solid ${border}` }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: ".12em", color: muted, textTransform: "uppercase", margin: 0 }}>Admin Panel</p>
          <h1 style={{ fontSize: "1.6rem", fontWeight: 500, color: gold, margin: "4px 0 0" }}>Dashboard</h1>
        </div>
        <div style={{ background: "#1a1200", border: `1px solid ${gold30}`, borderRadius: 20, padding: "6px 16px", fontSize: 12, color: gold, fontWeight: 500 }}>
          Live Data
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: "1.5rem", flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "7px 18px", borderRadius: 6, cursor: "pointer", fontSize: 12, letterSpacing: ".04em", transition: "all .15s",
            fontWeight: tab === t ? 600 : 500,
            border: `1px solid ${tab === t ? gold : "#222"}`,
            background: tab === t ? "#1a1200" : "#0f0f0f",
            color: tab === t ? gold : "#555",
          }}>
            {t}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "Overview" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10, marginBottom: "1.2rem" }}>
            <StatCard label="Total Earnings"        value={fmt$(overview.totalEarnings)}         sub="Lessons + Subscriptions"                       accent={gold} />
            <StatCard label="Lesson Earnings"       value={fmt$(overview.lessonEarnings)}        sub={`${overview.totalBookings} confirmed`}          accent="#22c55e" />
            <StatCard label="Subscription Revenue"  value={fmt$(overview.subscriptionEarnings)}  sub={`${overview.activeSubscriptions} active plans`} accent="#9090c0" />
            <StatCard label="Total Users"           value={overview.totalUsers}                  sub={`${overview.totalTeachers} teachers`}           accent={gold} />
          </div>

          <SectionTitle>Subscription Breakdown</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 10 }}>
            {Object.entries(subscriptions.breakdown).map(([plan, info]) => (
              <div key={plan} style={{ background: surface, border: `1px solid ${border}`, borderRadius: 10, padding: "1rem 1.2rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <Badge text={plan === "yearly" ? "VIP Yearly" : "Base Monthly"} type={plan === "yearly" ? "gold" : "silver"} />
                  <span style={{ fontSize: "1.1rem", fontWeight: 700, color: "#22c55e" }}>{fmt$(info.revenue)}</span>
                </div>
                <p style={{ fontSize: 12, color: faint, margin: 0 }}>{info.count} subscriber{info.count !== 1 ? "s" : ""}</p>
              </div>
            ))}
            {Object.keys(subscriptions.breakdown).length === 0 && (
              <p style={{ color: faint, fontSize: 13 }}>No subscriptions yet.</p>
            )}
          </div>
        </div>
      )}

      {/* SUBSCRIPTIONS */}
      {tab === "Subscriptions" && (
        <div>
          <SectionTitle>All Students ({subscriptions.list.length})</SectionTitle>
          {subscriptions.list.length === 0 ? <Empty text="No students found." /> : (
            <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: "hidden", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {["#","User","Email","Plan","Sub Price","Lessons","Spent","Expires"].map(h => <Th key={h}>{h}</Th>)}
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.list.map((sub, i) => {
                    const hasPlan = !!sub.plan;
                    const planType = sub.plan === "yearly" ? "gold" : sub.plan === "monthly" ? "silver" : "none";
                    const planLabel = sub.plan || "No Plan";
                    return (
                      <tr key={i}>
                        <Td isDim>{i + 1}</Td>
                        <Td>{sub.userName}</Td>
                        <Td isDim>{sub.userEmail}</Td>
                        <Td>
                          <Badge text={planLabel} type={planType} />
                        </Td>
                        <Td isGold={hasPlan} isDim={!hasPlan}>
                          {hasPlan ? fmt$(sub.price) : "—"}
                        </Td>
                        <Td>{sub.bookingCount}</Td>
                        <Td isGold={sub.totalSpent > 0}>{sub.totalSpent > 0 ? fmt$(sub.totalSpent) : "—"}</Td>
                        <Td isDim>{hasPlan ? fmtDate(sub.expiresAt) : "—"}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* TEACHERS */}
      {tab === "Teachers" && (
        <div>
          <SectionTitle>Teacher Earnings &amp; Stats</SectionTitle>
          {teacherStats.length === 0 ? <Empty text="No confirmed lessons yet." /> : (
            teacherStats.map((t, i) => (
              <div key={t.teacherId} style={{ background: surface, border: `1px solid ${border}`, borderRadius: 10, padding: "1.1rem 1.2rem", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: t.upcoming.length ? 10 : 0 }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#1a1200", border: `1px solid ${gold30}`, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 15, color: gold, flexShrink: 0 }}>
                    {t.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p style={{ fontSize: 14, fontWeight: 500, color: gold, margin: 0 }}>#{i + 1} {t.name}</p>
                    <p style={{ fontSize: 11, color: dimmer, margin: "2px 0 0" }}>{t.skills}</p>
                  </div>
                  <div style={{ marginLeft: "auto", textAlign: "right" }}>
                    <p style={{ fontSize: "1.1rem", fontWeight: 700, color: "#22c55e", margin: 0 }}>{fmt$(t.earnings)}</p>
                    <p style={{ fontSize: 11, color: faint, margin: "2px 0 0" }}>{t.lessons} lesson{t.lessons !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                {t.upcoming.length > 0 && (
                  <div style={{ background: deep, borderRadius: 6, padding: "8px 10px", border: `1px solid ${border}` }}>
                    <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: ".08em", textTransform: "uppercase", color: faint, marginBottom: 6 }}>Scheduled Sessions</p>
                    {t.upcoming.slice(0, 5).map((u, j) => (
                      <div key={j} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "3px 0", borderBottom: j < Math.min(t.upcoming.length, 5) - 1 ? "1px solid #111" : "none" }}>
                        <span style={{ fontSize: 11, color: muted }}>{fmtDate(u.date)} · {fmtTime(u.time)} · {u.duration}min</span>
                        <span style={{ fontSize: 11, color: "#8a7a3a", marginLeft: "auto" }}>{u.studentName}</span>
                        {u.zoomJoinUrl && (
                          <a href={u.zoomJoinUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, fontWeight: 600, color: "#2563eb", textDecoration: "none" }}>Zoom ↗</a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* UPCOMING */}
      {tab === "Upcoming" && (
        <div>
          <SectionTitle>Upcoming Sessions — Next 7 Days ({upcomingSessions.length})</SectionTitle>
          {upcomingSessions.length === 0 ? <Empty text="No sessions in the next 7 days." /> : (
            <div style={{ border: `1px solid ${border}`, borderRadius: 10, overflow: "hidden", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr>{["#","Student","Teacher","Date","Time","Duration","Zoom"].map(h => <Th key={h}>{h}</Th>)}</tr></thead>
                <tbody>
                  {upcomingSessions.map((u, i) => (
                    <tr key={i}>
                      <Td isDim>{i + 1}</Td>
                      <Td>{u.studentName}</Td>
                      <Td>{u.teacherName}</Td>
                      <Td isDim>{fmtDate(u.date)}</Td>
                      <Td isDim>{fmtTime(u.time)}</Td>
                      <Td>{u.duration} min</Td>
                      <Td>
                        {u.zoomJoinUrl
                          ? <a href={u.zoomJoinUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 600, color: "#2563eb", textDecoration: "none" }}>Join ↗</a>
                          : <span style={{ color: dimmer, fontSize: 12 }}>N/A</span>}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;